import { Component } from "react"
import { toast } from "react-toastify"
import { Container, Row, Col, Button, Form } from "react-bootstrap"
import { RouteComponentProps } from "react-router"
import { Subscription } from "rxjs"
import DonorInformation from "./DonorInformation"
import { DonorInformationModel, OTHER_REASON_TEXT, GetRegulatoryModeDonor, GovServiceDataModel, GovLabModel, GovPanelModel, ServiceTypeOld } from "./OrderModels"
import OrderRequestsHeader, { StepData } from "./OrderRequestsHeader"
import PotentialDonorMatchModal from "./PotentialDonorMatchModal"
import FormatDate from "../../formatters/FormatDate"
import FormatPhone from "../../formatters/FormatPhone"
import ActionButton from "../ActionButton"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { TestReasonsObservable, RegulatoryModeObservable } from "../../api/OrderRequests/OrderRequestsConstraintGet"
import { TestReasonType } from "../../api/OrderRequests/OrderRequestsModel/TestReasonType"
import { AdvanceSearchDonorGetObservable } from "../../api/OrderRequests/SearchDonorGet"
import { Donor } from "../../api/OrderRequests/OrderRequestsModel/Donor"
import OrderData from "../../api/OrderRequests/OrderRequestsModel/OrderData"
import { EventType, MaxDonorIdLength, StorageKey } from "../../config/Constants"
import GovCollection from "./GovCollection"
import GovOrderedService from "./GovOrderedService"
import { GovPanel, SampleType } from "../../api/Randoms/GovPoolModel/CollectionTypeResponse"
import LabAccount from "../../api/LabService/LabServiceModel/LabAccount"
import ConfirmationModal from "../ConfirmationModal"
import SaveOrder from "../../api/OrderRequests/OrderRequestsModel/SaveOrder"
import OrderSubmitObservable from "../../api/OrderRequests/OrderSubmit"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import AutoFilterCompaniesGetObservable from "../../api/Companies/AutoFilterCompaniesGet"
import GridRow from "../../api/GovDonor/GridRow"
import GovDevice from "../../api/Randoms/GovPoolModel/GovDevice"
import { OrdersRoute } from "../../config/Routes"
import FormatError from "../../formatters/FormatError"
import { addProcessingCollectionTest } from "../../api/CachedCollectionTest"
import GovPerformCollection, { GovPerformCollectionProps } from "./GovPerformCollection"
import { FullBaseUrl } from "../../api/ApiUrl"
import GovOrderDonorInfo from "./GovOrderDonorInfo"

interface GovOrderRequestProps {
  location: {
    state?: {
      formData?: GridRow[],
      pendingRandom?: boolean
    },
  }
}

interface GovOrderRequestData {
  company?: string,
  selectedCompany?: AutoFilterCompany
  companyID: number,
  reasonObj?: TestReasonType,
  reasonOther?: string
  reason?: string
  donor: DonorInformationModel
}

interface GovOrderRequestState extends GovOrderRequestData {
  formKey: string
  step: number
  infoHtml?: string
  isDonorInfoSuccess: boolean
  showPotentialMatches: boolean
  potentialDonorMatchList: Donor[]
  isDonorValid: boolean
  reasonsLoading: boolean
  companies: AutoFilterCompany[]
  reasons: TestReasonType[]
  idTypes: GetRegulatoryModeDonor[]
  orderSubmitting: boolean
  submittedOrder: boolean
  authorizationID?: number
  accessCode?: number
  showConfirmation: boolean
  serviceDOT?: boolean
  sampleType?: SampleType
  isPOCTDevice: boolean
  labAccount?: LabAccount
  panel?: GovPanel
  device?: GovDevice
  testDetailID: string
  showPerformCollection: boolean
}

export interface DonorData {
  PersonID: number
  FirstName: string
  LastName: string
  DonorID: string
  IDType: string
  MockDonorID: string
}

const RandomReason = "Random"

const emptyDonor: DonorInformationModel = {
  idType: undefined,
  idTypeObj: undefined,
  state: undefined,
  stateCode: undefined,
  donorId: undefined,
  firstName: undefined,
  middleInitial: undefined,
  lastName: undefined,
  eveningPhone: undefined,
  daytimePhone: undefined,
  birthdate: undefined,
  gender: undefined
}

const emptyData: GovOrderRequestData = {
  company: undefined,
  companyID: 0,
  selectedCompany: undefined,
  reasonObj: undefined,
  reasonOther: undefined,
  reason: undefined,
  donor: { ...emptyDonor }
}

const buildOrderRequestData = (orderFormData?: GridRow[]) => ({
  ...emptyData,
  company: orderFormData ? orderFormData[0]?.CompanyName : undefined,
  reason: orderFormData ? RandomReason : undefined,
  donor: {
    ...emptyDonor,
    idType: "EmployerID",
    donorId: orderFormData ? orderFormData[0]?.EmployerID.toString() : undefined,
  }
})

export default class GovOrderRequest extends Component<GovOrderRequestProps, GovOrderRequestState> {
  completingPendingOrders: boolean = false
  pendingRandom?: boolean
  serviceDOT?: boolean
  sampleType?: SampleType
  labAccount?: LabAccount
  panel?: GovPanel
  device?: GovDevice
  orderFormData?: GridRow[]
  testDetailID: string
  donorData?: DonorData
  accessCode?: number

  constructor(props: GovOrderRequestProps) {
    super(props)

    this.orderFormData = props.location.state?.formData
    this.pendingRandom = props.location.state?.pendingRandom
    if (this.orderFormData) {
      this.completingPendingOrders = (typeof this.pendingRandom === 'undefined') ? true : this.pendingRandom
      this.donorData = {
        PersonID: this.orderFormData[0].PersonID,
        FirstName: this.orderFormData[0].FirstName,
        LastName: this.orderFormData[0].LastName,
        DonorID: (this.orderFormData[0].EmployerID || "").toString(),
        IDType: "EmployerID",
        MockDonorID: (this.orderFormData[0].DonorID || "").toString(),
      }
      this.serviceDOT = this.orderFormData[0].DOT
      this.sampleType = { SampleTypeID: this.orderFormData[0].SampleTypeID } as SampleType
      this.labAccount = { LabAccount: this.orderFormData[0].LabAccountNumber } as LabAccount
      this.panel = { ID: this.orderFormData[0].Panel } as GovPanel
      this.device = { Code: this.orderFormData[0].Device } as GovDevice
      // If ad-hoc order, do not try to use an existing test detail id from a pending random test
      this.testDetailID = (this.completingPendingOrders == false) ? "" : (this.orderFormData[0].TestDetailID || "").toString() // TODO: verify
      this.accessCode = this.orderFormData[0].GovAccessCode
    } else {
      this.completingPendingOrders = false
      this.testDetailID = ""
      this.accessCode = 0 // TODO: pull from donor info endpoint
    }

    const data = buildOrderRequestData(this.orderFormData)
    this.state = {
      ...data,
      formKey: "GovOrderRequestForm",
      step: 1,
      infoHtml: undefined,
      isDonorInfoSuccess: false,
      showPotentialMatches: false,
      potentialDonorMatchList: [],
      isDonorValid: false,
      companies: [],
      reasonsLoading: false,
      reasons: [],
      idTypes: [],
      submittedOrder: false,
      orderSubmitting: false,
      showConfirmation: false,
      serviceDOT: this.serviceDOT,
      panel: this.panel,
      device: this.device,
      sampleType: this.sampleType,
      labAccount: this.labAccount,
      testDetailID: this.testDetailID,
      accessCode: this.accessCode,
      isPOCTDevice: false,
      showPerformCollection: false
    }
  }

  subscriptions = new Subscription()
  advanceSearchDonorSubscription?: Subscription

  componentDidMount() {
    this.setState({ reasonsLoading: true })
    this.subscriptions.add(AutoFilterCompaniesGetObservable("").subscribe(
      (data: AutoFilterCompany[]) => {
        const companyName = this.state.company || data[0]?.Name || ""
        this.setState({
          companies: data,
          company: companyName,
          companyID: data[0]?.AccountID || 0,
          selectedCompany: data.find((item: AutoFilterCompany) => item.Name === companyName)
        })
      }
    ))

    this.subscriptions.add(
      RegulatoryModeObservable(this.regulatoryModeFromServices()).subscribe(
        results => this.setState({ idTypes: results }),
        error => toast.error(error)
      )
    )

    this.subscriptions.add(
      TestReasonsObservable().subscribe(
        (results: TestReasonType[]) => this.setState({
          reasons: results,
          reasonsLoading: false,
          reasonObj: this.orderFormData && results.find(item => item.Description === "Random")
        }),
        error => toast.error(error)
      )
    )
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
    this.advanceSearchDonorSubscription && this.advanceSearchDonorSubscription.unsubscribe()
  }

  resetForm = () => {
    const data = buildOrderRequestData(this.orderFormData)
    const reasonObj = data.reason ? this.state.reasons.find(item => item.Description === data.reason) : undefined
    const idTypeObj = data.donor.idType ? this.state.idTypes.find(item => item.PrimaryIDType === data.donor.idType) : undefined

    this.setState({
      ...data,
      formKey: Math.random().toString(),
      reasonObj: reasonObj,
      donor: { ...data.donor, idTypeObj: idTypeObj },
      step: 1,
      infoHtml: undefined,
      isDonorInfoSuccess: false,
      showPotentialMatches: false,
      potentialDonorMatchList: [],
      isDonorValid: false,
      submittedOrder: false,
      orderSubmitting: false,
      isPOCTDevice: false,
      panel: this.panel,
      device: this.device,
      sampleType: this.sampleType,
      labAccount: this.labAccount,
    })
  }

  onReasonChange = (value: string) => {
    const reasonObject = this.state.reasons?.find((item: TestReasonType) => item.Description === value)
    this.setState({ reason: reasonObject?.Description, reasonObj: reasonObject, submittedOrder: false })
  }
  onDonorChange = (donor: DonorInformationModel, isValid: boolean, isDonorInfoSuccess: boolean) => this.setState({ donor: donor, accessCode: donor.accessCode, isDonorValid: isValid, isDonorInfoSuccess: isDonorInfoSuccess, submittedOrder: false })
  onInfoHtmlChange = (infoHtml?: string) => this.setState({ infoHtml: infoHtml, submittedOrder: false })

  onTypeChange = (type?: SampleType, isPOCTDevice?: boolean) => this.setState({ isPOCTDevice: !!isPOCTDevice, sampleType: type, labAccount: undefined, panel: undefined, device: undefined })
  onLabChange = (lab?: LabAccount) => this.setState({ labAccount: lab, panel: undefined, device: undefined })
  onPanelChange = (panel?: GovPanel) => this.setState({ panel: panel })
  onDeviceChange = (device?: GovDevice) => this.setState({ device: device })

  // Navigation methods
  onCancel = () => {
    localStorage.removeItem(StorageKey.SavedOrderData)
    localStorage.removeItem(StorageKey.CanViewOrderFinish)
    if (this.props.location.state?.formData) {
      (this.props as RouteComponentProps).history.goBack()
    } else {
      this.resetForm()
    }
  }

  onGoToStep1 = () => this.setState({ step: 1 })
  onGoToStep2 = () => this.setState({ step: 2 })

  onSubmit = () => {
    if (this.state.isDonorInfoSuccess) {
      this.saveOrder()
    }
    else {
      this.advanceSearchDonorSubscription && this.advanceSearchDonorSubscription.unsubscribe()
      const params = {
        CompanyID: this.state.selectedCompany?.AccountID || 0,
        DayTimePhone: this.state.donor?.daytimePhone || "",
        EveningPhone: this.state.donor?.eveningPhone || "",
        Mobile: null,
        RegulatoryModeID: this.regulatoryModeFromServices(),
        LastName: this.state.donor?.lastName,
        DateOfBirth: this.state.donor?.birthdate ? FormatDate(this.state.donor?.birthdate) : undefined
      }
      this.advanceSearchDonorSubscription = AdvanceSearchDonorGetObservable(params).subscribe(
        (response: Donor[]) => {
          if (response.length) {
            this.setState({ potentialDonorMatchList: response, showPotentialMatches: true })
          }
          else {
            this.saveOrder()
          }
        },
        (error: string) => toast.error(error)
      )
    }
  }

  regulatoryModeFromServices = () => 0  // TODO: verify
  getSampleTypeCode = (sampleType: string, isPOCT: boolean): EventType => {
    return sampleType.indexOf(EventType.Urine) !== -1 ?
      (isPOCT ? EventType.UrinePOCT : EventType.Urine) :
      (isPOCT ? EventType.SalivaDrugPOCT : EventType.SalivaDrug)
  }

  saveOrder() {
    const formatPhone = (phone?: string) => {
      if (!phone || phone === "np" || phone === "n/p" || phone === "NP" || phone === "N/P") {
        return "N/P"
      }
      return phone.replace(/-/g, "")
    }

    const formatDate = (date?: Date) => {
      if (!date) {
        return ""
      }
      return FormatDate(date)
    }


    const sampleTypeCode = this.getSampleTypeCode(this.state.sampleType?.LabCode?.toUpperCase() || "", this.state.isPOCTDevice)
    const order: SaveOrder = {
      MultipleDrug: [],
      MultipleAlcohol: [],
      MultiplePOCT: [],
      MultipleClinical: [],
      MultiplePhysical: {},
      POCTCount: 0,
      CollectionVendorID: "",
      FormFoxVendorAN: 0,
      Collector: {
        VendorID: 0,
        ParentVendorID: null,
        FFID: "",
        Name: "",
        Code: "",
        Type: null,
        Address1: "",
        Address2: "",
        City: "",
        State: "",
        Zip: "",
        Phone: "",
        HoursOfOperation: "",
        Private: false,
        Distance: 0,
        Latitude: 0,
        Longitude: 0,
        UseDOTForm: false,
        FFEnabled: false,
        OutOfNetwork: false,
        iVendorID: 0,
        SiteID: null,
        Fax: ""
      },
      CollectorPhone: null,
      CollectorVendorName: "",
      ConfirmDeviceCode: "",
      ConfirmDeviceDescription: "",
      ConfirmDeviceExpirationDate: null,
      ConfirmDeviceID: null,
      ConfirmDeviceSerialNumber: "",
      EventData: {
        AccountID: this.state.selectedCompany?.AccountID || 0,
        CompanyID: this.state.selectedCompany?.AccountID || 0,
        CompanyCode: this.state.selectedCompany?.Code || "",
        CompanyFullName: this.state.selectedCompany?.Code ? `${this.state.selectedCompany?.Name || ""} (${this.state.selectedCompany?.Code || ""})` : "",
        CompanyName: this.state.selectedCompany?.Name || "",
        DateOrdered: "",
        DateScheduled: "",
        ExpirationDate: "",
        LocationID: "",
        PrimaryID: (this.state.donor.donorId || "").replace(/-/g, ""),
        PrimaryIDRank: this.state.donor.idTypeObj?.Rank || 0,
        PrimaryIDType: this.state.donor.idTypeObj?.PrimaryIDType || "",
        RegulatoryModeID: this.regulatoryModeFromServices(),
        RegionID: "",
        RegionName: "",
        TimeZone: "",
        ProceedImmediately:false,
        ExtendExpirationHours: 0
      },
      Panel: {
        PanelID: "",
        Description: "",
        Code: "",
        ClientID: "",
        BeginEffectiveDate: "",
        EndEffectiveDate: "",
        UserLogin: "",
        UpdateMethod: "",
        UpdateDate: ""
      },
      PanelDescription: "",
      IsAlcohol: "",
      IsAssociatedWithPool: false,
      IsCompleted: false,
      IsDrug: "",
      IsBackground: false,
      IsNew: false,
      IsPhysical: false,
      IsAudiogram: false,
      IsPoct: false,
      IsRandom: false,
      IsReleased: false,
      LaboratoryVendorCode: "",
      LaboratoryVendorID: "",
      LaboratoryVendorName: "",
      LabPanelID: 0,
      TestReasonID: this.state.reasonObj?.TestReasonID || 0,
      TestReasonDescription: this.state.reasonObj?.Description || "",
      TestTypeDescription: "Drug",
      TestTypeID: 1,
      SampleTypeID: "",
      SampleTypeDescription: "",
      Labbased: true,
      OrderComment: "",
      SplitSpecimen: "",
      Observed: "",
      DOT: false,
      Normal: "",
      SampleTypeCode: sampleTypeCode,
      PersonDetail: {
        PersonID: this.state.donor.personId || 0,
        DateOfBirth: formatDate(this.state.donor.birthdate),
        HomePhone: "",
        CellPhone: formatPhone(this.state.donor.daytimePhone),
        WorkPhone: formatPhone(this.state.donor.eveningPhone),
        MiddleInitial: this.state.donor.middleInitial || "",
        AlternateID: "",
        DriverID: "",
        EmployerID: this.state.donor.donorId || "",
        SSN: "",
        FirstName: this.state.donor.firstName || "",
        LastName: this.state.donor.lastName || "",
        CCFPersonnelID: (this.state.donor.donorId || "").replace(/-/g, ""),
        DriverLicStateOfIssue: this.state.donor?.stateCode || this.state.donor?.state?.Abbreviation || "",
        Gender: this.state.donor.gender,
        Email: this.state.donor.email || ""
      },
      MockAlcoholTestDetailID: "",
      MockDrugTestDetailID: this.state.testDetailID,
      SSOToken: "",
      BaseUrl:  FullBaseUrl(),
      //TODO: verify PoolMembersID: this.orderFormData ? this.orderFormData[0]?.PoolMembersID : undefined
    }

    order.MultipleDrug.push(
      {
        DOT: this.state.labAccount?.Regulated === "Yes",
        IsPoct: false,
        SplitSpecimen: false,
        Observed: false,
        Normal: true,
        RegulatoryModeID: "",
        RegulatoryModeName: "",
        EmployeeCategoryID: "",
        EmployeeCategoryName: "",
        LabAccountNumber: this.state.labAccount?.LabAccount || "",
        LaboratoryVendorCode: this.state.labAccount?.LabCode || "",
        LaboratoryVendorID: this.state.labAccount?.LabClientMemberId || 0,
        LabPanelID: this.state.panel?.Name || "",
        PanelDescription: this.state.panel?.Name || "",
        PanelCode: this.state.panel?.ID || "",
        SampleTypeCode: sampleTypeCode,
        Device: this.state.device?.Code || ""
      }
    )

    this.setState({ orderSubmitting: true })
    this.subscriptions.add(
      OrderSubmitObservable(order).subscribe(
        (results: OrderData) => {
          this.setState({ submittedOrder: true, orderSubmitting: false, accessCode: results.GovAccessCode })
          this.saveSuccess(results)

        },
        error => {
          this.setState({ submittedOrder: false, orderSubmitting: false })
          toast.error(FormatError(error))
        }
      )
    )
  }

  saveSuccess = (orderFinishData: OrderData) => {
    localStorage.setItem(StorageKey.SavedOrderData, JSON.stringify(orderFinishData))
    if (orderFinishData.FormFoxTestAN > 0 || orderFinishData.ProfileNumber) {
      if (orderFinishData.FormFoxTestAN === 0) {
        toast.error(orderFinishData.FFresult)
      }
      localStorage.setItem(StorageKey.CanViewOrderFinish, JSON.stringify(true))
      localStorage.setItem(StorageKey.FromSelectionInbox, JSON.stringify(!!this.orderFormData))
    } else {
      toast.error(orderFinishData.FFresult)
    }
    this.setState({ authorizationID: orderFinishData.FormFoxTestAN })
    if (orderFinishData.FormFoxTestAN > 0) {
      this.setState({ showConfirmation: true })
    }
  }

  closePotentialDonorMatchModal = () => {
    //this.PersonID = 0
    toast.error("Donor was not found. New donor will be created.")
    this.setState({ showPotentialMatches: false }, () => this.saveOrder())
  }

  submitPotentialDonorMatchModal = (response: Donor) => {
    toast.success("Your Order will be placed with the selected ID Type.")
    const fetchedDate = response.DateOfBirth ? new Date(response.DateOfBirth) : undefined
    const validDate = fetchedDate && fetchedDate.getTime && !isNaN(fetchedDate.getTime())
    const newDonor: DonorInformationModel = {
      ...this.state.donor,
      personId: response.PersonID,
      firstName: response.FirstName,
      middleInitial: response.MiddleInitial,
      lastName: response.LastName,
      eveningPhone: FormatPhone(response.WorkPhone),
      daytimePhone: FormatPhone(response.CellPhone),
      birthdate: validDate ? fetchedDate : undefined,

      firstNameReadOnly: !!response.FirstName,
      middleInitialReadOnly: !!response.MiddleInitial,
      lastNameReadOnly: !!response.LastName,
      homePhoneReadOnly: !!(response.HomePhone && response.HomePhone !== "N/P"),
      workPhoneReadOnly: !!(response.WorkPhone && response.WorkPhone !== "N/P"),
      state: undefined,
      stateCode: response.DriverLICStateOfIssue ? response.DriverLICStateOfIssue : undefined
    }
    this.setState({
      donor: newDonor,
      showPotentialMatches: false
    }, () => this.saveOrder())
  }

  performCollection = () => {
    this.setState({ showConfirmation: false, showPerformCollection: true })
  }

  closeConfirmation = () => {
    this.setState({ showConfirmation: false })
    this.onCancel();
    (this.props as RouteComponentProps).history.push(OrdersRoute)
  }

  closePerformCollection = () => {
    this.setState({ showPerformCollection: false });
    (this.props as RouteComponentProps).history?.push(OrdersRoute)
  }

  createGovPerformCollectionProps = (): GovPerformCollectionProps => {


    let testDetailID: number = 0
    let testDetailIDHash: string = ""
    let referenceID: number = 0
    let labCode: string = ""
    let sampleID: string = ""
    let tempInRange: number = 0
    let personID: number = 0
    let firstName: string = ""
    let lastName: string = ""
    let dateOfBirth: Date | undefined
    let gender: string = ""
    let sampleTypeCode = this.getSampleTypeCode(this.state.sampleType?.LabCode?.toUpperCase() || "", this.state.isPOCTDevice)
    let DeviceID: number = 0
    let AccountID: number = 0

    const authorizationID: number = this.state.authorizationID || 0
    if (authorizationID > 0) {

      const orderString = localStorage.getItem(StorageKey.SavedOrderData) || ""
      const orderData: OrderData = JSON.parse(orderString)
      if (orderData) {

        testDetailID = orderData.TestDetailID || 0
        if (testDetailID === 0) {
          testDetailID = orderData.DrugTestDetailID || 0
        }
        if (testDetailID === 0) {
          if (Array.isArray(orderData.DrugTestDetailIDList)) {
            if (orderData.DrugTestDetailIDList.length > 0) {
              testDetailID = orderData.DrugTestDetailIDList[0]
            }
          }
        }
      }
      testDetailIDHash = this.state.testDetailID || this.testDetailID
      referenceID = this.state.authorizationID || 0
      labCode = this.state.labAccount?.LabAccount || ""
      sampleID = ""
      tempInRange = 0
      personID = this.state.donor.personId || 0
      firstName = this.state.donor.firstName || ""
      lastName = this.state.donor.lastName || ""
      dateOfBirth = this.state.donor.birthdate
      gender = this.state.donor.gender || ""
      DeviceID = this.state.device?.DeviceID || 0
      AccountID = this.state.companyID
    }
    const result: GovPerformCollectionProps = {
      testDetailID: testDetailID,
      testDetailIDHash: testDetailIDHash,
      referenceID: referenceID,
      sampleTypeCode: sampleTypeCode,
      sampleTypeID: 0,
      labCode: labCode,
      sampleID: sampleID,
      tempInRange: tempInRange,
      personID: personID,
      firstName: firstName,
      lastName: lastName,
      dateOfBirth: dateOfBirth,
      gender: gender,
      onHide: () => this.closePerformCollection(),
      DeviceID: DeviceID,
      AccountID: AccountID
    }
    return result
  }

  render() {
    const { reasonObj, reasonOther, submittedOrder, isPOCTDevice, sampleType, labAccount, panel, device } = this.state
    const isIssuerValid = !!reasonObj && (reasonObj?.Description !== OTHER_REASON_TEXT || !!reasonOther)
    const panelSelected = !!panel?.ID && (!isPOCTDevice || (isPOCTDevice && !!device?.Code))
    const isPanelValid = !!sampleType?.SampleTypeID && !!labAccount?.LabAccount && panelSelected
    const canGoToOrderFeatures = isIssuerValid && isPanelValid && this.state.isDonorValid
    const isStep2Available = this.state.step !== 2 && canGoToOrderFeatures
    const isOrderCanBeSubmitted = canGoToOrderFeatures && !submittedOrder
    const isBirthDateRequired = true

    const performCollectionProps = this.createGovPerformCollectionProps()


    const headerSteps: StepData[] = [
      {
        title: '1',
        disabled: this.state.step === 1,
        current: this.state.step === 1,
        onClick: () => this.setState({ step: 1 })
      },
      {
        title: '2',
        disabled: !isStep2Available,
        current: this.state.step === 2,
        onClick: () => this.setState({ step: 2 })
      }
    ]

    const service: GovServiceDataModel = {
      serviceType: ServiceTypeOld.Drug,
      sampleType: sampleType?.LabCode,
      isDOT: labAccount?.Regulated === "Yes",
      panelCode: panel?.ID,
      panelDescription: panel?.Name,
      testToPerform: panel?.Name,
      labAccount: labAccount?.LabAccount,
      labCode: labAccount?.LabCode || "",
      labClientMemberId: labAccount?.LabClientMemberId || 0
    }

    return (<>
      <AppPageWrapper parentClassName="OrderRequest" showNav={true}>
        <PotentialDonorMatchModal
          potentialDonorMatchList={this.state.potentialDonorMatchList}
          show={this.state.showPotentialMatches}
          onClose={this.closePotentialDonorMatchModal}
          onSubmit={this.submitPotentialDonorMatchModal} />
        <Container fluid className="OrderRequests" key={this.state.formKey}>
          <Row>
            <Col md={12}>
              <Container fluid className="mb-4">
                <Row>
                  <Col xs={12} md={6} className="p-0 px-2 mx-1">
                    <OrderRequestsHeader steps={headerSteps} />
                  </Col>
                  <Col xs={12} md={6} hidden={!this.state.infoHtml}>
                    <div className="ServiceLongDescription" dangerouslySetInnerHTML={{ __html: this.state.infoHtml || "" }} />
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
          <Row>
            <Col className="p-0" hidden={this.state.step !== 1}>
              <Container fluid>
                <Col xs={12} sm={12} md={6}>
                  <DonorInformation companyId={this.state.selectedCompany?.AccountID}
                    completingPendingOrders={!!this.orderFormData}
                    orderFormData={this.donorData}
                    donor={this.state.donor}
                    onDonorChange={this.onDonorChange}
                    isBirthDateRequired={isBirthDateRequired}
                    idTypes={this.state.idTypes}
                    showIdType={false}
                    showMiddleInitial={false}
                    eveningPhoneLabel="WORK PHONE"
                    daytimePhoneLabel="MOBILE PHONE"
                    eveningPhoneField="WorkPhone"
                    daytimePhoneField="CellPhone"
                    daytimePhoneRequired={false}
                    maxDonorIdLength={MaxDonorIdLength}
                    showGenderField
                    validateDonorIdFormat
                  />
                </Col>
              </Container>
            </Col>
          </Row>
          <Row>
            <Col hidden={this.state.step !== 1}>
              <Container fluid>
                <Row>
                  <Col sm={12} md={6} lg={4}>
                    <Form>
                      <Form.Group>
                        <Form.Label className="font-weight-bold required">REASON</Form.Label>
                        <Form.Control as="select" value={this.state.reasonObj?.Description}
                          onChange={(e) => this.onReasonChange(e.target.value)}
                          disabled={this.state.reasonsLoading || this.completingPendingOrders}>
                          <option />
                          {this.state.reasons.map((item: TestReasonType) => <option value={item.Description} key={item.Description}>{item.Description}</option>)}
                        </Form.Control>
                      </Form.Group>
                      <Form.Group hidden={this.state.reasonObj?.Description !== OTHER_REASON_TEXT}>
                        <Form.Label className="font-weight-bold required">OTHER REASON</Form.Label>
                        <Form.Control type="text"
                          value={this.state.reasonOther || ""}
                          onChange={(e) => this.setState({ reasonOther: e.target.value })} />
                      </Form.Group>
                      {!!this.state.selectedCompany && <GovCollection showHeading
                        labelClassName="text-uppercase font-weight-bold"
                        editing
                        pendingRandom={this.pendingRandom}
                        labAccount={this.state.labAccount?.LabAccount}
                        serviceDOT={this.state.serviceDOT || false}
                        sampleTypeID={this.state.sampleType?.SampleTypeID}
                        panel={this.state.panel?.ID}
                        device={this.state.device?.Code}
                        companyID={this.state.companyID}
                        onTypeChange={this.onTypeChange}
                        onLabChange={this.onLabChange}
                        onPanelChange={this.onPanelChange}
                        onDeviceChange={this.onDeviceChange}
                      />}
                    </Form>
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
          <Row hidden={this.state.step !== 2}>
            <Col>
              <h4 className="primary text-uppercase">
                <span>AUTHORIZATION # </span>
                <span className="authorizationNumber"> ({this.state.authorizationID})</span>
              </h4>
            </Col>
          </Row>
          {this.state.step === 2 && <GovOrderDonorInfo {...this.state.donor} accessCode={this.state.accessCode} />}
          <Row hidden={this.state.step !== 2}>
            <Col sm={12} md={6} lg={8} className="d-flex align-items-center">
              <GovOrderedService service={service} />
            </Col>
          </Row>
          <Row hidden={this.state.step !== 2}>
            <Col md={12} className="test-property">Confirm your order</Col>
          </Row>
          <Container fluid>
            <Row>
              <Col xs={12} md={6} lg={6} className="d-flex pt-2 pr-1">
                <Button hidden={this.state.step !== 1} onClick={this.onCancel} variant="secondary" className="mr-auto fixed-w10">CANCEL</Button>
                <Button hidden={this.state.step !== 2} onClick={this.onGoToStep1} variant="secondary" className="mr-auto fixed-w10">BACK</Button>
                <Button hidden={this.state.step !== 1} onClick={this.onGoToStep2} disabled={!isStep2Available} variant="primary" className="ml-auto fixed-w10">NEXT</Button>
                <ActionButton hidden={this.state.step !== 2} onClick={this.onSubmit} disabled={!isOrderCanBeSubmitted} spin={this.state.orderSubmitting} variant="primary" className="ml-auto fixed-w10" disableOnSpin>FINISH</ActionButton>
              </Col>
            </Row>
          </Container>
        </Container>
        <ConfirmationModal show={this.state.showConfirmation}
          title=""
          textTitle=""
          text="Do you want to perform the collection now?"
          showImage={false}
          cancelButtonText="No"
          confirmButtonText="Yes"
          onConfirm={() => this.performCollection()}
          onHide={() => this.closeConfirmation()} />
        {this.state.showPerformCollection && this.state.authorizationID &&
          <GovPerformCollection
            testName={service.panelDescription}
            testDetailID={performCollectionProps.testDetailID}
            testDetailIDHash={performCollectionProps.testDetailIDHash}
            referenceID={performCollectionProps.referenceID}
            sampleTypeCode={performCollectionProps.sampleTypeCode}
            sampleTypeID={performCollectionProps.sampleTypeID}
            labCode={performCollectionProps.labCode}
            sampleID={performCollectionProps.sampleID}
            tempInRange={performCollectionProps.tempInRange}
            firstName={performCollectionProps.firstName}
            lastName={performCollectionProps.lastName}
            dateOfBirth={performCollectionProps.dateOfBirth}
            gender={performCollectionProps.gender}
            onHide={() => performCollectionProps.onHide()}
            DeviceID={performCollectionProps.DeviceID}
            AccountID={performCollectionProps.AccountID}>
          </GovPerformCollection>}
      </AppPageWrapper>
    </>)
  }
}
